import { Controller } from "@hotwired/stimulus"

/**
 * Employer dashboard — the "one clock" (Direction A, "Live wire").
 *
 * The design's whole idea is that one event moves four surfaces at once. In the
 * approved mock that event was a fabricated applicant arrival on a setInterval:
 * a row slid in, the Applicants counter incremented, the header count followed,
 * and the job's row flashed while its chip bumped. Production has no live
 * channel for this page, so nothing here fabricates an arrival.
 *
 * WHAT THE CLOCK IS INSTEAD: the page load, and the event is the newest row the
 * server already rendered. On connect, in one shared timeline:
 *
 *   beat 0  every `count` target tweens 0 -> the value the SERVER rendered
 *           (KPI rail, the header's "N active / M candidates", the pipeline
 *           buckets). The header therefore moves with the rail, which is the
 *           mock's coupling.
 *   beat 1  the feed reveals newest-first, one row per 70ms, opacity only.
 *   beat 2  the newest row's dot fires a single arrival ring AND the jobs-table
 *           row for that same event's job flashes while its applicant chip
 *           rings. The job id comes from @recent_activity (data-empw-job), so
 *           the link is the controller's own data, not a title match.
 *
 * INVARIANTS
 * · No number ever passes the server's value, and the tween's last frame is the
 *   server's exact display string (data-empw-final), delimiters included.
 * · No setInterval, no repeating timer, nothing that keeps ticking. One pass.
 * · The feed list is armed (opacity 0) in the SAME task that schedules the
 *   reveal, so a JS failure or a missing pack leaves every row visible rather
 *   than blank.
 * · Under prefers-reduced-motion nothing starts: no rAF, no timeout, no class.
 *   The CSS block declares its resting states outright (see app.tailwind.css) —
 *   collapsing animation-duration does NOT yield an end state.
 * · Motion is paint only. Rows fade, dots ring via box-shadow, rows flash via
 *   background-color. No transform on anything an edge aligns to.
 *
 * DROPPED as un-honest against real data: the recurring fake arrivals and their
 * +1 increments to the Applicants counter, the "N new this week" hint and the
 * per-row applicant chips, plus the loop's periodic reset. Also the feed's
 * beating "LIVE" pill, which promised a push stream.
 */
export default class extends Controller {
  static targets = ["count", "feed", "feedRow", "jobRow"]

  // ms
  static ROW_STAGGER = 70
  static COUNT_MIN = 700
  static COUNT_MAX = 1500
  // Matches `.empw-rowbtn.is-ring { animation: empw-ring .9s … both }` — `both`
  // means the ring holds its end state, so the class has to come back off.
  static RING_MS = 900

  connect() {
    // A Map keyed by the element being tweened, not an array. The array version
    // pushed a NEW id on every frame and nothing ever removed one, so a normal
    // dashboard load left ~1350 dead handles in it and disconnect() called
    // cancelAnimationFrame on 1350 frames that had already fired. One live
    // handle per tweened element — replaced each frame, deleted when the tween
    // lands — so the Map is bounded by the count targets (10 on this page) and
    // is read as well as written: an element already mid-tween is skipped
    // instead of being tweened twice by a second connect().
    this.frames = new Map()
    this.timers = []

    if (this.prefersReducedMotion) return

    this.countUp()
    this.revealFeed()
  }

  disconnect() {
    this.frames.forEach((id) => cancelAnimationFrame(id))
    this.timers.forEach((id) => clearTimeout(id))
    this.frames.clear()
    this.timers = []
  }

  get prefersReducedMotion() {
    return (
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    )
  }

  // ── beat 0: every number counts up to the value the server rendered ───────
  countUp() {
    this.countTargets.forEach((el) => {
      const to = parseInt(el.dataset.empwValue, 10)
      const final = el.dataset.empwFinal
      if (!Number.isFinite(to) || final === undefined) return
      // Nothing to reveal, and starting a tween at 0 -> 0 would blank the glyph
      // for a frame.
      if (to === 0) return

      // Bigger numbers get a longer tween, so a 4-digit view count and a
      // single-digit job count still finish together-ish rather than one
      // snapping instantly.
      const duration = Math.min(
        this.constructor.COUNT_MAX,
        this.constructor.COUNT_MIN + Math.log10(to + 1) * 260
      )
      this.tween(el, to, final, duration)
    })
  }

  tween(el, to, final, duration) {
    // Already counting: leave the tween that owns this element alone rather
    // than racing a second one against it.
    if (this.frames.has(el)) return

    let start = null
    el.textContent = "0"

    const step = (ts) => {
      if (start === null) start = ts
      const p = Math.min(1, (ts - start) / duration)
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3)
      if (p < 1) {
        el.textContent = Math.round(to * eased).toLocaleString("en-US")
        this.frames.set(el, requestAnimationFrame(step))
      } else {
        // Land on the server's own string, never on a locale guess.
        el.textContent = final
        this.frames.delete(el)
      }
    }
    this.frames.set(el, requestAnimationFrame(step))
  }

  // ── beats 1 + 2: the feed reveals, then the newest event rings ────────────
  revealFeed() {
    const rows = this.feedRowTargets
    if (rows.length === 0) return

    // Arm and schedule in the same task: if this controller never boots, the
    // class is never added and every row stays visible.
    if (this.hasFeedTarget) this.feedTarget.classList.add("is-armed")

    rows.forEach((row, i) => {
      this.timers.push(
        setTimeout(() => row.classList.add("is-in"), i * this.constructor.ROW_STAGGER)
      )
    })

    // The newest row is rows[0]; its ring is CSS (.is-in.is-new .empw-tl-dot),
    // so the fourth surface only has to fire on the same beat.
    this.timers.push(setTimeout(() => this.flashNewestJob(rows[0]), 0))
  }

  flashNewestJob(newestRow) {
    const jobId = newestRow && newestRow.dataset.empwJob
    if (!jobId) return // a bundle purchase has no job row to flash

    // The table shows @jobs.first(8); an older job simply isn't there.
    const row = this.jobRowTargets.find((tr) => tr.dataset.empwJob === jobId)
    if (!row) return

    row.classList.add("is-flash")
    this.timers.push(setTimeout(() => row.classList.remove("is-flash"), this.constructor.RING_MS))

    // is-ring comes back OFF. Its keyframes are `both`, so the fill mode held
    // the ring's end state on the chip for the rest of the page's life — the row
    // beside it un-flashed after 900ms while the chip kept a permanent halo, so
    // "this is the newest event" stopped being a moment and became decoration.
    // Same 900ms as the flash it fires with, and the timer is tracked so
    // disconnect() cancels it.
    const chip = row.querySelector("[data-empw-chip]")
    if (!chip) return
    chip.classList.add("is-ring")
    this.timers.push(setTimeout(() => chip.classList.remove("is-ring"), this.constructor.RING_MS))
  }
}
